import { useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router'
import { ApiError, get, patch, post, type AuditEntry } from '../../api'
import { useSession } from '../../session'
import { Button, Chip, Icon, Quota, Toggle } from '../../ui'
import { AdminUserBilling } from '../UserBilling'
import { ConfirmChange } from '../Confirm'
import { AccessCell } from './List'
import { DailyUsage } from './DailyUsage'
import { userMarkLine, type FeatureStep } from '../../live-ui/watermark'
import { Statements } from './Statements'
import {
  can,
  day,
  endsText,
  hm,
  planTier,
  utc,
  type AdminBroadcast,
  type AdminBroadcastPage,
  type AdminQuota,
  type AdminUserOverview,
  type Permission,
} from './model'

/*
 * A user's card in the console (BILL-B6a, design §10.3): tabs Overview · Plan · Usage ·
 * Statements (BILL-REL) · Broadcasts · Activity (ContentTabs .sx-tabs.fit). Plan is the B2 page unchanged (its actions,
 * second factor, reasons and words). One request for the card (…/overview); older broadcasts by
 * cursor. No stream key, platform key or token is shown — the API sends counts only.
 */

export type CardTab = 'overview' | 'plan' | 'usage' | 'statements' | 'broadcasts' | 'activity'
const TABS: { id: CardTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'plan', label: 'Plan' },
  { id: 'usage', label: 'Usage' },
  { id: 'statements', label: 'Statements' },
  { id: 'broadcasts', label: 'Broadcasts' },
  { id: 'activity', label: 'Activity' },
]

export function AdminUserCard({ tab: fixed }: { tab?: CardTab }) {
  const { id = '', tab: param } = useParams()
  const tab: CardTab = fixed ?? (TABS.some((t) => t.id === param) ? (param as CardTab) : 'overview')
  const nav = useNavigate()
  const [days, setDays] = useState<30 | 90>(30)
  const ov = useQuery({
    queryKey: ['admin-user-overview', id, days],
    queryFn: () => get<AdminUserOverview>(`/api/admin/users/${id}/overview?days=${days}`),
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
    retry: false,
  })
  const u = ov.data
  const move = (to: CardTab) => nav(to === 'overview' ? `/admin/users/${id}` : `/admin/users/${id}/${to}`)
  const onKey = (e: React.KeyboardEvent, at: number) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!d) return
    e.preventDefault()
    const next = TABS[(at + d + TABS.length) % TABS.length]!
    move(next.id)
    ;(e.currentTarget.parentElement?.children[TABS.indexOf(next)] as HTMLElement | undefined)?.focus()
  }

  if (ov.error) {
    const missing = ov.error instanceof ApiError && ov.error.status === 404
    return (
      <div className="flex flex-col gap-3">
        <Crumb email={`user ${id}`} />
        <p role="alert" className="text-ui-body-sm text-fail">{missing ? 'No such user.' : `The card did not load: ${(ov.error as Error).message}`}</p>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-3" data-testid="user-card">
      <Crumb email={u?.email ?? `user ${id}`} />
      {u && (
        <div className="flex flex-wrap items-start gap-3">
          <div>
            <div className="flex items-baseline gap-2">
              <h1 className="text-ui-heading font-medium" data-testid="card-email">{u.email}</h1>
              <span className="sx-mono text-ui-caption text-muted">{u.id}</span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {u.billing_summary && <span className={`sx-plan ${planTier(u.billing_summary.plan_id)}`}>{u.billing_summary.plan_name}</span>}
              {u.billing_summary?.source === 'grant' && <span className="sx-tag">GIFT</span>}
              {u.billing_summary?.vip && <span className="sx-plan business">VIP</span>}
              {u.billing_summary && <span className="text-ui-caption text-muted">{sourceWords(u)}</span>}
              <AccessCell u={u} />
              {u.live && <Chip state="live" size="sm" still>ON AIR</Chip>}
            </div>
          </div>
          <span className="flex-1" />
          <BlockButton u={u} onDone={() => void ov.refetch()} />
        </div>
      )}
      <nav className="sx-tabs fit" role="tablist" aria-label="User card">
        {TABS.map((t, i) => (
          <a
            key={t.id}
            role="tab"
            href={t.id === 'overview' ? `/admin/users/${id}` : `/admin/users/${id}/${t.id}`}
            aria-selected={tab === t.id}
            tabIndex={tab === t.id ? 0 : -1}
            className={tab === t.id ? 'on' : undefined}
            data-testid={`tab-${t.id}`}
            onClick={(e) => {
              e.preventDefault()
              move(t.id)
            }}
            onKeyDown={(e) => onKey(e, i)}
          >
            {t.label}
            {t.id === 'broadcasts' && !!u?.broadcasts_30d && <small>{u.broadcasts_30d}</small>}
          </a>
        ))}
      </nav>
      <div role="tabpanel">
        {tab === 'plan' ? (
          <AdminUserBilling />
        ) : !u ? (
          <p className="text-ui-body-sm text-muted">Loading the card…</p>
        ) : tab === 'overview' ? (
          <Overview u={u} onChanged={() => void ov.refetch()} />
        ) : tab === 'usage' ? (
          <DailyUsage u={u} days={days} onDays={setDays} />
        ) : tab === 'statements' ? (
          <Statements u={u} />
        ) : tab === 'broadcasts' ? (
          <Broadcasts u={u} />
        ) : (
          <Activity u={u} />
        )}
      </div>
    </div>
  )
}

function Crumb({ email }: { email: string }) {
  return (
    <div className="sx-crumb">
      <Link to="/admin/users" className="text-text-soft hover:text-accent hover:underline">Users</Link>
      <Icon name="chevron-right" size={12} />
      <b>{email}</b>
    </div>
  )
}

function sourceWords(u: AdminUserOverview): string {
  const b = u.billing_summary!
  const st = b.subscription_status ? ` · ${b.subscription_status.replace('_', ' ')}` : ''
  return { subscription: `Subscription${st}`, free: 'Free floor', grant: 'Gift', legacy: 'Legacy', ambassador: 'Ambassador' }[b.source]
}

/** Buttons stay visible without the right, disabled with what they need (RBAC-1). */
function useRight(p: Permission): { ok: boolean; title?: string } {
  const { user } = useSession()
  const ok = can(user, p)
  return { ok, title: ok ? undefined : `Needs the ${p} permission` }
}

function BlockButton({ u, onDone }: { u: AdminUserOverview; onDone: () => void }) {
  const qc = useQueryClient()
  const right = useRight('users.manage')
  const [ask, setAsk] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  if (u.role === 'admin') return null
  const run = async (path: string, body?: object) => {
    setBusy(true)
    setErr('')
    try {
      await post(`/api/admin/users/${u.id}/${path}`, body)
      setAsk(false)
      setReason('')
      await qc.invalidateQueries({ queryKey: ['admin-users-page'] })
      onDone()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  if (u.status === 'blocked') {
    return (
      <Button size="sm" disabled={!right.ok || busy} title={right.title} onClick={() => void run('unblock')} data-testid="user-unblock">
        Unblock user
      </Button>
    )
  }
  if (!ask) {
    return (
      <Button size="sm" disabled={!right.ok} title={right.title} onClick={() => setAsk(true)} data-testid="user-block">
        Block user
      </Button>
    )
  }
  return (
    <div role="alertdialog" aria-label={`Block ${u.email}?`} className="flex max-w-[440px] flex-col gap-2 rounded-md border-[3px] border-double border-fail/45 p-3 text-ui-body-sm" data-testid="user-block-confirm">
      <p className="font-medium text-text">Block {u.email}?</p>
      <p className="text-text-soft">Their sessions end, signed-in apps are signed out and every broadcast of theirs stops now. Reconnects are refused.</p>
      <input className="sx-input sans" placeholder="Reason (optional)" aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      {err && <p role="alert" className="text-fail">{err}</p>}
      <div className="flex gap-2">
        <Button size="sm" variant="danger" busy={busy} onClick={() => void run('block', { reason })} data-testid="user-block-apply">Block user and stop broadcasts</Button>
        <Button size="sm" onClick={() => setAsk(false)}>Cancel</Button>
      </div>
    </div>
  )
}

function Panel({ title, aside, children, testId }: { title: string; aside?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <section className="sx-panel flex flex-col gap-2.5" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className="sx-label">{title}</span>
        {aside}
      </div>
      {children}
    </section>
  )
}

function KV({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[140px_minmax(0,1fr)] gap-x-3 gap-y-2 text-ui-body-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted">{k}</dt>
          <dd className="m-0 flex flex-wrap items-center gap-1.5 text-text-soft">{v}</dd>
        </div>
      ))}
    </dl>
  )
}

const mono = (s: ReactNode) => <span className="sx-mono">{s}</span>

/**
 * WM3 (WMU-09): the free plan's Strevix mark for this user — the center's flag (the operator's own
 * /api/me watermark), the user's features and who decided no_watermark (the plan or a manual grant),
 * from the same billing card the Plan tab reads.
 */
function useMark(id: number, billing: boolean) {
  const { user } = useSession()
  const flag = user?.watermark
  const card = useQuery({
    queryKey: ['admin-billing', String(id)],
    queryFn: () => get<{ entitlements: { features: string[]; breakdown: Record<string, FeatureStep[]> } }>(`/api/admin/users/${id}/billing`),
    enabled: !!flag && billing,
    retry: false,
  })
  const e = card.data?.entitlements
  return userMarkLine(flag, e?.features, e?.breakdown?.no_watermark)
}

function Overview({ u, onChanged }: { u: AdminUserOverview; onChanged: () => void }) {
  const b = u.billing_summary
  const plats = Object.entries(u.destinations_by_platform.by_platform)
  const ends = endsText(b)
  const mark = useMark(u.id, !!b)
  const markRow: [string, ReactNode][] = mark ? [['Strevix mark', <span key="w" data-testid="card-wm" data-on={mark.on} className={mark.on ? 'text-text-soft' : 'text-muted'}>{mark.text.replace(/^Strevix mark: /, '')}</span>]] : []
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title="Profile" testId="card-profile">
          <KV
            rows={[
              ['E-mail', mono(u.email)],
              ['User ID', mono(u.id)],
              ['Created', mono(utc(u.created_at))],
              ['Last sign-in', u.last_sign_in_at ? mono(utc(u.last_sign_in_at)) : <span className="text-muted">No session now</span>],
              ['Account', <AccessCell key="a" u={u} />],
              ['Outputs', <span key="o">{mono(u.destinations_by_platform.count)}{plats.length > 0 && <span className="text-muted"> · {plats.map(([p, n]) => `${platformName(p)} ${n}`).join(' · ')}</span>}</span>],
              ['Signed-in apps', mono(u.devices)],
              ['API tokens', mono(u.api_tokens)],
            ]}
          />
        </Panel>
        <Panel title="Plan" testId="card-plan">
          {b ? (
            <>
              <KV
                rows={[
                  ['Plan', <span key="p" className="inline-flex items-center gap-1.5"><span className={`sx-plan ${planTier(b.plan_id)}`}>{b.plan_name}</span><span className="sx-mono text-ui-caption text-muted">v{b.plan_version}</span></span>],
                  ['Source', sourceWords(u)],
                  ['Period', mono(`${day(b.usage_period.start)} – ${day(b.usage_period.end)}`)],
                  ['Ends', ends ? <span key="e" className={`sx-mono ${ends.warn ? 'text-warn' : ''}`}>{ends.text}</span> : '—'],
                  ['Billing', <span key="s" className="text-ui-caption text-muted">Stripe customer and invoices — after Stripe (B4)</span>],
                  ...markRow,
                ]}
              />
              <div><Link className="sx-btn sm" to={`/admin/users/${u.id}/plan`} data-testid="open-plan">Open plan and rights</Link></div>
            </>
          ) : (
            <p className="text-ui-body-sm text-muted">Plans need the billing.read permission and billing on (BILLING_MODE).</p>
          )}
        </Panel>
        <Panel title="Usage this period" aside={b && <span className="text-ui-caption text-muted">{b.usage_computed_at ? `Counted ${utc(b.usage_computed_at)} · every minute on air` : 'Nothing counted this period yet'}</span>} testId="card-usage">
          {b ? (
            <div className="flex flex-col gap-3.5">
              <QuotaMeter q={b.usage.stream_hours_month} label="Stream hours" icon="signal" resets={b.usage_period.end} />
              <QuotaMeter q={b.usage.captions_hours_month} label="Captions" icon="captions" resets={b.usage_period.end} note="Quality counts ×1.5" />
            </div>
          ) : (
            <p className="text-ui-body-sm text-muted">Usage needs the billing.read permission.</p>
          )}
        </Panel>
        <Panel title="Restrictions" testId="card-restrictions">
          {b && (b.access !== 'full' || b.has_holds) ? (
            <div className="flex flex-col gap-1.5 text-ui-body-sm">
              <AccessCell u={u} />
              <Link className="text-accent hover:underline" to={`/admin/users/${u.id}/plan`}>See holds and lift them on the Plan tab</Link>
            </div>
          ) : (
            <div className="sx-empty !py-4">
              <div className="ic"><Icon name="check" size={24} /></div>
              <h4>No restrictions</h4>
              <p>Holds and frozen accounts show here with their reason and end date.</p>
            </div>
          )}
        </Panel>
      </div>
      <Features u={u} onChanged={onChanged} />
    </div>
  )
}

function platformName(p: string): string {
  const known: Record<string, string> = { youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick', facebook: 'Facebook', instagram: 'Instagram', custom: 'Custom', tiktok: 'TikTok', x: 'X' }
  return known[p] ?? p
}

function QuotaMeter({ q, label, icon, resets, note }: { q?: AdminQuota; label: string; icon: 'signal' | 'captions'; resets: string; note?: string }) {
  if (!q || q.limit === 0) return <p className="text-ui-body-sm text-muted">{label}: not on the plan.</p>
  if (q.limit === null) {
    return <p className="text-ui-body-sm text-text-soft">{label}: <span className="sx-mono text-text">{hm(q.used)}</span> · no limit</p>
  }
  const left = Math.max(0, q.limit - q.used)
  return (
    <Quota
      label={label}
      icon={icon}
      used={Math.floor(q.used / 60)}
      max={Math.floor(q.limit / 60)}
      value={hm(q.used)}
      of={`of ${hm(q.limit)}`}
      left={left === 0 ? 'Used up' : `${hm(left)} left`}
      sub={note ? `${note} · resets ${day(resets)}` : `Resets ${day(resets)}`}
      state={q.level >= 80 ? 'warn' : undefined}
    />
  )
}

type Flag = 'fill' | 'captions' | 'caption_translation' | 'ru'
const FLAGS: { k: Flag; label: string; hint: string }[] = [
  { k: 'fill', label: 'Bitrate fill', hint: 'The destination editor offers the bitrate choice and the Live cards show the fill.' },
  { k: 'captions', label: 'Captions', hint: 'Captions in the speaker’s language for this account.' },
  { k: 'caption_translation', label: 'Caption translation', hint: 'Translated captions; needs Captions.' },
  { k: 'ru', label: 'Russian', hint: 'The cabinet may be in Russian for this user (LEGAL: only under this flag).' },
]

/** Console features an admin turns on (PATCH …/features, audited); bitrate fill asks first. */
function Features({ u, onChanged }: { u: AdminUserOverview; onChanged: () => void }) {
  const right = useRight('users.manage')
  const [ask, setAsk] = useState<{ k: Flag; on: boolean } | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const send = async (k: Flag, on: boolean) => {
    setBusy(true)
    setErr('')
    try {
      await patch(`/api/admin/users/${u.id}/features`, { [k]: on })
      setAsk(null)
      onChanged()
    } catch (e) {
      setErr(`${FLAGS.find((f) => f.k === k)!.label} not changed: ${(e as Error).message}`)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Panel title="Console features" aside={!right.ok && <span className="text-ui-caption text-muted">{right.title}</span>} testId="card-features">
      <div className="grid gap-2 sm:grid-cols-2">
        {FLAGS.map((f) => {
          const on = !!u.features?.[f.k as keyof NonNullable<typeof u.features>]
          const testId = f.k === 'fill' ? `user-fill-${u.id}` : f.k === 'captions' ? `captions-${u.email}` : `user-${f.k}-${u.id}`
          return (
            <label key={f.k} className="flex items-start gap-2.5 text-ui-body-sm" title={f.hint}>
              <Toggle
                checked={on}
                size="sm"
                disabled={!right.ok || busy}
                aria-label={`${f.label} for ${u.email}`}
                data-testid={testId}
                data-on={on}
                onChange={(next) => (f.k === 'fill' ? setAsk({ k: 'fill', on: next }) : void send(f.k, next))}
              />
              <span className="flex flex-col">
                <span className={on ? 'text-text' : 'text-text-soft'}>{f.label}</span>
                <span className="text-ui-caption text-muted">{f.hint}</span>
              </span>
            </label>
          )
        })}
      </div>
      {err && <p role="alert" className="text-ui-body-sm text-fail">{err}</p>}
      {ask && (
        <ConfirmChange
          testId="user-fill-confirm"
          title={`${ask.on ? 'Turn on' : 'Turn off'} bitrate fill for ${u.email}?`}
          apply={ask.on ? 'Turn on' : 'Turn off'}
          busy={busy}
          onApply={() => void send('fill', ask.on)}
          onCancel={() => setAsk(null)}
        >
          <p>
            {ask.on
              ? 'Their destination editor offers the bitrate choice, and the Live cards show the actual bitrate and the share of filler. Start with test accounts.'
              : 'The bitrate choice leaves their console. Fill already set for their destinations stays in force on the servers.'}
          </p>
          <p>Recorded in the log.</p>
        </ConfirmChange>
      )}
    </Panel>
  )
}

function Broadcasts({ u }: { u: AdminUserOverview }) {
  const [more, setMore] = useState<AdminBroadcast[]>([])
  const [cursor, setCursor] = useState(u.broadcasts.next_cursor)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const items = [...u.broadcasts.items, ...more.filter((m) => !u.broadcasts.items.some((x) => x.id === m.id))]
  const load = async () => {
    setBusy(true)
    setErr('')
    try {
      const p = await get<AdminBroadcastPage>(`/api/admin/users/${u.id}/broadcasts?limit=20&before=${encodeURIComponent(cursor)}`)
      setMore((m) => [...m, ...p.items])
      setCursor(p.next_cursor)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  if (items.length === 0) {
    return (
      <div className="sx-empty">
        <div className="ic"><Icon name="signal" size={24} /></div>
        <h4>No broadcasts yet</h4>
        <p>Broadcasts of the last 90 days show here when this user goes live.</p>
      </div>
    )
  }
  return (
    <div className="sx-panel overflow-x-auto !p-0">
      <table className="sx-table compact" data-testid="broadcasts-table">
        <thead>
          <tr>
            <th>Started (UTC)</th>
            <th className="num">Duration</th>
            <th>Stream</th>
            <th>Node</th>
            <th className="num">Outputs</th>
            <th className="num">Captions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.id} data-testid="broadcast-row">
              <td className="sx-mono">{utc(b.started_at).replace(' UTC', '')}</td>
              <td className="num">{hm(b.seconds)}</td>
              <td>
                <span className="inline-flex items-center gap-2">
                  {b.live && <Chip state="live" size="sm" still>ON AIR</Chip>}
                  <span className="sx-mono">{b.stream_name}</span>
                </span>
              </td>
              <td className="sx-mono">{b.node || '—'}</td>
              <td className="num">{b.outputs}</td>
              <td className="num">{b.captions_seconds ? hm(b.captions_seconds) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-2 border-t border-line px-3 py-2">
        <span className="text-ui-caption text-muted">{items.length} shown · broadcasts are kept 90 days</span>
        <span className="flex-1" />
        {err && <span role="alert" className="text-ui-caption text-fail">{err}</span>}
        {cursor && <Button size="sm" busy={busy} onClick={() => void load()} data-testid="broadcasts-more">Load more</Button>}
      </div>
    </div>
  )
}

const ACTIONS: Record<string, { word: string; state?: 'warn' | 'fail' | 'live' }> = {
  'admin.user_blocked': { word: 'User blocked', state: 'fail' },
  'admin.user_unblocked': { word: 'User unblocked', state: 'live' },
  'billing.plan_granted': { word: 'Plan given for a time' },
  'billing.trial_extended': { word: 'Trial extended' },
  'entitlements.ambassador_on': { word: 'Made ambassador' },
  'entitlements.ambassador_off': { word: 'Ambassador removed' },
  'entitlements.layer_added': { word: 'Limit or feature changed' },
  'entitlements.layer_revoked': { word: 'Change revoked' },
  'entitlements.hold_added': { word: 'Hold added', state: 'warn' },
  'entitlements.hold_lifted': { word: 'Hold lifted' },
}

function Activity({ u }: { u: AdminUserOverview }) {
  const right = useRight('billing.read')
  if (!u.activity) {
    return <p className="text-ui-body-sm text-muted">{right.ok ? 'Billing is off on this center: no billing journal.' : 'The billing journal needs the billing.read permission.'}</p>
  }
  if (u.activity.length === 0) {
    return (
      <div className="sx-empty">
        <div className="ic"><Icon name="check" size={24} /></div>
        <h4>No changes yet</h4>
        <p>Plan changes, holds, blocks and their reasons show here, newest first.</p>
      </div>
    )
  }
  return (
    <div className="sx-panel !p-0">
      <div className="sx-feed" data-testid="activity">
        {u.activity.map((a: AuditEntry) => {
          const w = ACTIONS[a.action] ?? { word: a.action }
          const reason = typeof a.details?.reason === 'string' ? a.details.reason : ''
          return (
            <div key={a.id} className="sx-ev" data-state={w.state} data-testid="activity-row">
              <span className="tm" title={utc(a.created_at)}>{day(a.created_at)}</span>
              <Icon name={w.state === 'fail' ? 'state-fail' : w.state === 'warn' ? 'state-warn' : w.state === 'live' ? 'state-live' : 'plan'} size={16} />
              <div>
                <b>{w.word}</b> <span className="d">{a.actor ? `by ${a.actor}` : ''}{reason ? ` — “${reason}”` : ''}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
